

import React, { useEffect, useMemo, useState } from 'react';
import productService from '../services/productService';
import batchService from '../services/batchService';

const INITIAL_INVOICE = {
  dealer_name: '',
  dealer_mobile: '',
  dealer_address: '',
  dealer_gst_number: '',
  gst_enabled: true,
  supply_type: 'intra_state',
  notes: '',
};

const INITIAL_ITEM = {
  product_id: '',
  batch_id: '',
  quantity: '1',
  unit_price: '0',
  discount_amount: '0',
  gst_rate: '0',
};

const money = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const getProductId = (product) => product.product_id ?? product.id;
const getBatchId = (batch) => batch.batch_id ?? batch.id;

const getProductName = (product) =>
  product.product_name ?? product.name ?? '';

const getBatchNumber = (batch) =>
  batch.batch_number ?? batch.batch_no ?? '';

const getBatchStock = (batch) =>
  toNumber(
    batch.quantity_remaining ??
      batch.available_quantity ??
      batch.quantity_available ??
      batch.current_stock ??
      batch.quantity ??
      0
  );

const getProductUnit = (product) => product.unit ?? 'pcs';

const calculateItem = (item, invoice) => {
  const quantity = toNumber(item.quantity);
  const unitPrice = toNumber(item.unit_price);
  const discountInput = toNumber(item.discount_amount);

  const subtotal = quantity * unitPrice;

  const discount = Math.min(
    Math.max(discountInput, 0),
    Math.max(subtotal, 0)
  );

  const taxableAmount = Math.max(0, subtotal - discount);

  const gstRate = invoice.gst_enabled
    ? toNumber(item.gst_rate)
    : 0;

  const totalTax = taxableAmount * (gstRate / 100);

  const cgstAmount =
    invoice.supply_type === 'intra_state' ? totalTax / 2 : 0;

  const sgstAmount =
    invoice.supply_type === 'intra_state' ? totalTax / 2 : 0;

  const igstAmount =
    invoice.supply_type === 'inter_state' ? totalTax : 0;

  return {
    subtotal,
    discount_amount: discount,
    taxable_amount: taxableAmount,
    cgst_amount: cgstAmount,
    sgst_amount: sgstAmount,
    igst_amount: igstAmount,
    total_tax: totalTax,
    line_total: taxableAmount + totalTax,
  };
};

const calculateInvoice = (items, invoice) => {
  const totals = items.reduce(
    (result, item) => {
      const calculated = calculateItem(item, invoice);

      result.subtotal += calculated.subtotal;
      result.discount_amount += calculated.discount_amount;
      result.taxable_amount += calculated.taxable_amount;
      result.cgst_amount += calculated.cgst_amount;
      result.sgst_amount += calculated.sgst_amount;
      result.igst_amount += calculated.igst_amount;
      result.total_tax += calculated.total_tax;
      result.total_before_rounding += calculated.line_total;

      return result;
    },
    {
      subtotal: 0,
      discount_amount: 0,
      taxable_amount: 0,
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      total_tax: 0,
      total_before_rounding: 0,
    }
  );

  const totalAmount = Math.round(totals.total_before_rounding);

  return {
    ...totals,
    round_off: totalAmount - totals.total_before_rounding,
    total_amount: totalAmount,
  };
};

const InvoiceForm = ({
  initialValues,
  firmDetails,
  onSubmit,
  submitLabel = 'Create Invoice',
  loading = false,
  onLoadBatches,
}) => {
  const [invoice, setInvoice] = useState(() => ({
    ...INITIAL_INVOICE,
    ...initialValues,
    dealer_name: initialValues?.dealer_name ?? '',
    dealer_mobile: initialValues?.dealer_mobile ?? '',
    dealer_address: initialValues?.dealer_address ?? '',
    dealer_gst_number: initialValues?.dealer_gst_number ?? '',
    gst_enabled: initialValues?.gst_enabled ?? true,
    supply_type: initialValues?.supply_type ?? 'intra_state',
    notes: initialValues?.notes ?? '',
  }));

  const [items, setItems] = useState(() =>
    initialValues?.items?.length
      ? initialValues.items.map((item) => ({
          ...INITIAL_ITEM,
          ...item,
          product_id: String(item.product_id ?? ''),
          batch_id: String(item.batch_id ?? ''),
          quantity: String(item.quantity ?? 1),
          unit_price: String(item.unit_price ?? 0),
          discount_amount: String(item.discount_amount ?? 0),
          gst_rate: String(item.gst_rate ?? 0),
        }))
      : [{ ...INITIAL_ITEM }]
  );

  const [products, setProducts] = useState([]);
  const [batchesByProduct, setBatchesByProduct] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingBatchProducts, setLoadingBatchProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      setLoadingProducts(true);

      try {
        const result = await productService.getProducts({
          page: 1,
          limit: 100,
          is_active: true,
        });

        const list = Array.isArray(result)
          ? result
          : result?.products ??
            result?.data?.products ??
            result?.data?.data ??
            [];

        if (!cancelled) {
          setProducts(
            list.filter((product) => product.is_active !== false)
          );
        }
      } catch (error) {
        if (!cancelled) {
          setPageError(
            error.response?.data?.message ||
              'Unable to load products. Please try again.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingProducts(false);
        }
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(
    () => calculateInvoice(items, invoice),
    [items, invoice.gst_enabled, invoice.supply_type]
  );

  const updateInvoice = (event) => {
    const { name, value, type, checked } = event.target;

    setInvoice((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: '',
    }));

    setPageError('');
  };

  const updateItem = (index, field, value) => {
    setItems((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );

    setErrors((previous) => ({
      ...previous,
      [`item_${index}_${field}`]: '',
      items: '',
    }));

    setPageError('');
  };

  async function loadBatchesForProduct(productId) {
    if (!productId) return;

    const key = String(productId);

    if (
      Object.prototype.hasOwnProperty.call(batchesByProduct, key) ||
      loadingBatchProducts.includes(key)
    ) {
      return;
    }

    setLoadingBatchProducts((previous) =>
      previous.includes(key) ? previous : [...previous, key]
    );

    try {
      const result = onLoadBatches
        ? await onLoadBatches(productId)
        : await batchService.getBatches({
            product_id: productId,
            page: 1,
            limit: 100,
          });

      const list = Array.isArray(result)
        ? result
        : result?.batches ??
          result?.data?.batches ??
          result?.data?.data ??
          [];

      setBatchesByProduct((previous) => ({
        ...previous,
        [key]: list,
      }));
    } catch (error) {
      setPageError(
        error.response?.data?.message ||
          'Unable to load batches for the selected product.'
      );
    } finally {
      setLoadingBatchProducts((previous) =>
        previous.filter((id) => id !== key)
      );
    }
  }

  // Load batches for existing items when editing an invoice.
  useEffect(() => {
    const productIds = [
      ...new Set(items.map((item) => item.product_id).filter(Boolean)),
    ];

    productIds.forEach((productId) => {
      loadBatchesForProduct(productId);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProductChange = async (index, productId) => {
    const product = products.find(
      (entry) => String(getProductId(entry)) === String(productId)
    );

    setItems((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...INITIAL_ITEM,
              product_id: productId,
              quantity: item.quantity || '1',
              unit_price: String(
                product?.selling_price ?? product?.sale_price ?? 0
              ),
              gst_rate: String(product?.gst_rate ?? 0),
            }
          : item
      )
    );

    setErrors((previous) => ({
      ...previous,
      [`item_${index}_product_id`]: '',
      [`item_${index}_batch_id`]: '',
      [`item_${index}_quantity`]: '',
      items: '',
    }));

    setPageError('');

    if (productId) {
      await loadBatchesForProduct(productId);
    }
  };

  const handleBatchChange = (index, batchId) => {
    const item = items[index];

    const batchList =
      batchesByProduct[String(item.product_id)] ?? [];

    const batch = batchList.find(
      (entry) => String(getBatchId(entry)) === String(batchId)
    );

    const product = products.find(
      (entry) =>
        String(getProductId(entry)) === String(item.product_id)
    );

    setItems((previous) =>
      previous.map((entry, itemIndex) =>
        itemIndex === index
          ? {
              ...entry,
              batch_id: batchId,
              unit_price: String(
                batch?.selling_price ??
                  batch?.sale_price ??
                  product?.selling_price ??
                  product?.sale_price ??
                  0
              ),
              gst_rate: String(
                batch?.gst_rate ?? product?.gst_rate ?? 0
              ),
            }
          : entry
      )
    );

    setErrors((previous) => ({
      ...previous,
      [`item_${index}_batch_id`]: '',
      [`item_${index}_quantity`]: '',
      items: '',
    }));

    setPageError('');
  };

  const addItem = () => {
    if (items.length >= 200) {
      setErrors((previous) => ({
        ...previous,
        items: 'An invoice cannot contain more than 200 items.',
      }));
      return;
    }

    setItems((previous) => [...previous, { ...INITIAL_ITEM }]);
  };

  const removeItem = (index) => {
    setItems((previous) =>
      previous.length === 1
        ? previous
        : previous.filter((_, itemIndex) => itemIndex !== index)
    );

    setErrors({});
    setPageError('');
  };

  const validate = () => {
    const nextErrors = {};

    if (
      !invoice.dealer_name.trim() ||
      invoice.dealer_name.trim().length > 200
    ) {
      nextErrors.dealer_name =
        'Customer name is required and must not exceed 200 characters.';
    }

    if (
      invoice.dealer_mobile &&
      invoice.dealer_mobile.length > 20
    ) {
      nextErrors.dealer_mobile =
        'Mobile number must not exceed 20 characters.';
    }

    if (
      invoice.dealer_address &&
      invoice.dealer_address.length > 500
    ) {
      nextErrors.dealer_address =
        'Address must not exceed 500 characters.';
    }

    if (
      invoice.dealer_gst_number &&
      invoice.dealer_gst_number.trim().length > 15
    ) {
      nextErrors.dealer_gst_number =
        'GST number must not exceed 15 characters.';
    }

    if (!items.length || items.length > 200) {
      nextErrors.items = 'Add between 1 and 200 invoice items.';
    }

    items.forEach((item, index) => {
      const prefix = `item_${index}_`;

      const productId = Number(item.product_id);
      const batchId = Number(item.batch_id);
      const quantity = Number(item.quantity);
      const unitPrice = Number(item.unit_price);
      const discount = Number(item.discount_amount);
      const gstRate = Number(item.gst_rate);

      if (!Number.isSafeInteger(productId) || productId <= 0) {
        nextErrors[`${prefix}product_id`] = 'Select a valid product.';
      }

      if (!Number.isSafeInteger(batchId) || batchId <= 0) {
        nextErrors[`${prefix}batch_id`] = 'Select a valid batch.';
      }

      if (
        !/^\d+(\.\d{1,3})?$/.test(String(item.quantity)) ||
        !Number.isFinite(quantity) ||
        quantity <= 0 ||
        quantity > 1e9
      ) {
        nextErrors[`${prefix}quantity`] =
          'Quantity must be positive with at most 3 decimal places.';
      }

      if (
        !Number.isFinite(unitPrice) ||
        unitPrice < 0 ||
        unitPrice > 1e12
      ) {
        nextErrors[`${prefix}unit_price`] =
          'Enter a valid non-negative unit price.';
      }

      if (
        !Number.isFinite(discount) ||
        discount < 0 ||
        discount > 1e12
      ) {
        nextErrors[`${prefix}discount_amount`] =
          'Enter a valid non-negative discount.';
      }

      if (
        !Number.isFinite(gstRate) ||
        gstRate < 0 ||
        gstRate > 100
      ) {
        nextErrors[`${prefix}gst_rate`] =
          'GST must be between 0 and 100.';
      }

      const batchList =
        batchesByProduct[String(item.product_id)] ?? [];

      const batch = batchList.find(
        (entry) => String(getBatchId(entry)) === String(item.batch_id)
      );

      if (
        batch &&
        Number.isFinite(quantity) &&
        quantity > getBatchStock(batch)
      ) {
        nextErrors[`${prefix}quantity`] =
          `Only ${getBatchStock(batch)} units are available.`;
      }

      if (
        Number.isFinite(discount) &&
        Number.isFinite(quantity) &&
        Number.isFinite(unitPrice) &&
        discount > quantity * unitPrice
      ) {
        nextErrors[`${prefix}discount_amount`] =
          'Discount cannot exceed the subtotal.';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPageError('');

    if (loadingBatchProducts.length > 0) {
      setPageError('Please wait until product batches finish loading.');
      return;
    }

    if (!validate()) {
      setPageError('Please correct the highlighted fields.');
      return;
    }

    const payloadItems = items.map((item) => {
      const product = products.find(
        (entry) =>
          String(getProductId(entry)) === String(item.product_id)
      );

      const batchList =
        batchesByProduct[String(item.product_id)] ?? [];

      const batch = batchList.find(
        (entry) => String(getBatchId(entry)) === String(item.batch_id)
      );

      const calculated = calculateItem(item, invoice);

      return {
        product_id: Number(item.product_id),
        batch_id: Number(item.batch_id),
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        discount_amount: calculated.discount_amount,
        gst_rate: invoice.gst_enabled ? Number(item.gst_rate) : 0,
        ...calculated,
        product_name: getProductName(product ?? {}),
        sku: product?.sku ?? '',
        batch_number: getBatchNumber(batch ?? {}),
        hsn_code: batch?.hsn_code ?? product?.hsn_code ?? '',
        unit: getProductUnit(product ?? {}),
      };
    });

    const calculatedTotals = calculateInvoice(items, invoice);

    // Invoice number, invoice date, due date, and firm ID are
    // intentionally omitted: the backend manages invoice generation.
    const payload = {
      dealer_name: invoice.dealer_name.trim(),
      dealer_mobile: invoice.dealer_mobile.trim() || null,
      dealer_address: invoice.dealer_address.trim() || null,
      dealer_gst_number:
        invoice.dealer_gst_number.trim().toUpperCase() || null,

      gst_enabled: Boolean(invoice.gst_enabled),
      supply_type: invoice.supply_type,
      notes: invoice.notes.trim() || null,

      subtotal: calculatedTotals.subtotal,
      discount_amount: calculatedTotals.discount_amount,
      taxable_amount: calculatedTotals.taxable_amount,
      cgst_amount: calculatedTotals.cgst_amount,
      sgst_amount: calculatedTotals.sgst_amount,
      igst_amount: calculatedTotals.igst_amount,
      total_tax: calculatedTotals.total_tax,
      round_off: calculatedTotals.round_off,
      total_amount: calculatedTotals.total_amount,

      items: payloadItems,
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      setPageError(
        error.response?.data?.message ||
          error.message ||
          'Unable to create invoice.'
      );
    }
  };

  const renderFieldError = (name) =>
    errors[name] ? (
      <small className="invoice-field-error" role="alert">
        {errors[name]}
      </small>
    ) : null;

  if (loadingProducts) {
    return (
      <div className="invoice-form-card">
        Loading products...
      </div>
    );
  }

  return (
    <form className="invoice-form-card" onSubmit={handleSubmit}>
      {pageError && (
        <div className="invoice-alert invoice-alert-error" role="alert">
          {pageError}
        </div>
      )}

      <section className="invoice-form-section">
        <h2>Customer Details</h2>

        <div className="invoice-form-grid">
          <label className="invoice-form-field invoice-form-field-full">
            <span>Customer Name *</span>
            <input
              type="text"
              name="dealer_name"
              value={invoice.dealer_name}
              onChange={updateInvoice}
              placeholder="Enter customer name"
              maxLength={200}
              required
              disabled={loading}
              aria-invalid={Boolean(errors.dealer_name)}
            />
            {renderFieldError('dealer_name')}
          </label>

          <label className="invoice-form-field">
            <span>Mobile Number</span>
            <input
              type="tel"
              name="dealer_mobile"
              value={invoice.dealer_mobile}
              onChange={updateInvoice}
              placeholder="Customer mobile number"
              maxLength={20}
              disabled={loading}
              aria-invalid={Boolean(errors.dealer_mobile)}
            />
            {renderFieldError('dealer_mobile')}
          </label>

          <label className="invoice-form-field invoice-form-field-full">
            <span>Address</span>
            <textarea
              name="dealer_address"
              value={invoice.dealer_address}
              onChange={updateInvoice}
              placeholder="Customer address"
              maxLength={500}
              rows={3}
              disabled={loading}
              aria-invalid={Boolean(errors.dealer_address)}
            />
            {renderFieldError('dealer_address')}
          </label>

          {/* Customer GST number only; PAN is intentionally omitted. */}
          <label className="invoice-form-field invoice-form-field-full">
            <span>GST Number</span>
            <input
              type="text"
              name="dealer_gst_number"
              value={invoice.dealer_gst_number}
              onChange={updateInvoice}
              placeholder="Enter customer GST number (optional)"
              maxLength={15}
              autoCapitalize="characters"
              disabled={loading}
              aria-invalid={Boolean(errors.dealer_gst_number)}
            />
            {renderFieldError('dealer_gst_number')}
          </label>
        </div>
      </section>

      <section className="invoice-form-section">
        <div className="invoice-form-section-heading">
          <div>
            <h2>Invoice Items</h2>
            <p>Select products and batches, then enter quantities.</p>
          </div>

          <button
            type="button"
            className="invoice-secondary-button"
            onClick={addItem}
            disabled={loading || items.length >= 200}
          >
            + Add Item
          </button>
        </div>

        {errors.items && (
          <p className="invoice-field-error" role="alert">
            {errors.items}
          </p>
        )}

        {items.map((item, index) => {
          const productBatches =
            batchesByProduct[String(item.product_id)] ?? [];

          const batchLoading = loadingBatchProducts.includes(
            String(item.product_id)
          );

          const itemTotals = calculateItem(item, invoice);

          const selectedBatch = productBatches.find(
            (batch) =>
              String(getBatchId(batch)) === String(item.batch_id)
          );

          return (
            <div className="invoice-item-card" key={index}>
              <div className="invoice-item-heading">
                <h3>Item {index + 1}</h3>

                <button
                  type="button"
                  className="invoice-remove-item-button"
                  onClick={() => removeItem(index)}
                  disabled={items.length === 1 || loading}
                >
                  Remove
                </button>
              </div>

              <div className="invoice-form-grid">
                <label className="invoice-form-field">
                  <span>Product *</span>
                  <select
                    value={item.product_id}
                    onChange={(event) =>
                      handleProductChange(index, event.target.value)
                    }
                    disabled={loading}
                    required
                    aria-invalid={Boolean(
                      errors[`item_${index}_product_id`]
                    )}
                  >
                    <option value="">Select product</option>
                    {products.map((product) => (
                      <option
                        key={getProductId(product)}
                        value={getProductId(product)}
                      >
                        {getProductName(product)}
                        {product.sku ? ` (${product.sku})` : ''}
                      </option>
                    ))}
                  </select>
                  {renderFieldError(`item_${index}_product_id`)}
                </label>

                <label className="invoice-form-field">
                  <span>Batch *</span>
                  <select
                    value={item.batch_id}
                    onChange={(event) =>
                      handleBatchChange(index, event.target.value)
                    }
                    disabled={
                      loading || !item.product_id || batchLoading
                    }
                    required
                    aria-invalid={Boolean(
                      errors[`item_${index}_batch_id`]
                    )}
                  >
                    <option value="">
                      {batchLoading ? 'Loading batches...' : 'Select batch'}
                    </option>
                    {productBatches.map((batch) => (
                      <option
                        key={getBatchId(batch)}
                        value={getBatchId(batch)}
                      >
                        {getBatchNumber(batch) || getBatchId(batch)}
                        {' — Stock: '}
                        {getBatchStock(batch)}
                        {batch.expiry_date
                          ? ` — Expiry: ${String(batch.expiry_date).slice(0, 10)}`
                          : ''}
                      </option>
                    ))}
                  </select>
                  {renderFieldError(`item_${index}_batch_id`)}
                </label>

                <label className="invoice-form-field">
                  <span>Quantity *</span>
                  <input
                    type="number"
                    min="0.001"
                    max="1000000000"
                    step="0.001"
                    value={item.quantity}
                    onChange={(event) =>
                      updateItem(index, 'quantity', event.target.value)
                    }
                    disabled={loading}
                    required
                    aria-invalid={Boolean(
                      errors[`item_${index}_quantity`]
                    )}
                  />
                  {renderFieldError(`item_${index}_quantity`)}
                  {selectedBatch && (
                    <small>
                      Available stock: {getBatchStock(selectedBatch)}
                    </small>
                  )}
                </label>

                <label className="invoice-form-field">
                  <span>Unit Price (₹) *</span>
                  <input
                    type="number"
                    min="0"
                    max="1000000000000"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(event) =>
                      updateItem(index, 'unit_price', event.target.value)
                    }
                    disabled={loading}
                    required
                    aria-invalid={Boolean(
                      errors[`item_${index}_unit_price`]
                    )}
                  />
                  {renderFieldError(`item_${index}_unit_price`)}
                </label>

                <label className="invoice-form-field">
                  <span>Discount (₹)</span>
                  <input
                    type="number"
                    min="0"
                    max="1000000000000"
                    step="0.01"
                    value={item.discount_amount}
                    onChange={(event) =>
                      updateItem(
                        index,
                        'discount_amount',
                        event.target.value
                      )
                    }
                    disabled={loading}
                    aria-invalid={Boolean(
                      errors[`item_${index}_discount_amount`]
                    )}
                  />
                  {renderFieldError(`item_${index}_discount_amount`)}
                </label>

                <label className="invoice-form-field">
                  <span>GST Rate (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={invoice.gst_enabled ? item.gst_rate : '0'}
                    onChange={(event) =>
                      updateItem(index, 'gst_rate', event.target.value)
                    }
                    disabled={loading || !invoice.gst_enabled}
                    aria-invalid={Boolean(
                      errors[`item_${index}_gst_rate`]
                    )}
                  />
                  {renderFieldError(`item_${index}_gst_rate`)}
                </label>
              </div>

              <div className="invoice-item-calculated">
                <span>Taxable: {money(itemTotals.taxable_amount)}</span>
                <span>Tax: {money(itemTotals.total_tax)}</span>
                <strong>Line Total: {money(itemTotals.line_total)}</strong>
              </div>
            </div>
          );
        })}
      </section>

      <section className="invoice-form-section">
        <h2>Tax Settings</h2>

        <div className="invoice-form-grid">
          <label className="invoice-form-field">
            <span>Supply Type</span>
            <select
              name="supply_type"
              value={invoice.supply_type}
              onChange={updateInvoice}
              disabled={loading}
            >
              <option value="intra_state">
                Intra-state (CGST + SGST)
              </option>
              <option value="inter_state">Inter-state (IGST)</option>
            </select>
          </label>

          <label className="invoice-form-checkbox">
            <input
              type="checkbox"
              name="gst_enabled"
              checked={invoice.gst_enabled}
              onChange={updateInvoice}
              disabled={loading}
            />
            <span>Enable GST</span>
          </label>
        </div>
      </section>

      <section className="invoice-form-section">
        <h2>Notes</h2>

        <label className="invoice-form-field">
          <span>Additional Notes</span>
          <textarea
            name="notes"
            value={invoice.notes}
            onChange={updateInvoice}
            rows={2}
            placeholder="Optional notes for this invoice"
            disabled={loading}
          />
        </label>
      </section>

      <section className="invoice-form-section invoice-form-totals">
        <h2>Invoice Summary</h2>

        <div>
          <span>Subtotal</span>
          <strong>{money(totals.subtotal)}</strong>
        </div>

        <div>
          <span>Discount</span>
          <strong>{money(totals.discount_amount)}</strong>
        </div>

        <div>
          <span>Taxable Amount</span>
          <strong>{money(totals.taxable_amount)}</strong>
        </div>

        {invoice.gst_enabled &&
          (invoice.supply_type === 'intra_state' ? (
            <>
              <div>
                <span>CGST</span>
                <strong>{money(totals.cgst_amount)}</strong>
              </div>
              <div>
                <span>SGST</span>
                <strong>{money(totals.sgst_amount)}</strong>
              </div>
            </>
          ) : (
            <div>
              <span>IGST</span>
              <strong>{money(totals.igst_amount)}</strong>
            </div>
          ))}

        <div>
          <span>Total Tax</span>
          <strong>{money(totals.total_tax)}</strong>
        </div>

        <div>
          <span>Round Off</span>
          <strong>{money(totals.round_off)}</strong>
        </div>

        <div className="invoice-form-grand-total">
          <span>Grand Total</span>
          <strong>{money(totals.total_amount)}</strong>
        </div>
      </section>

      <div className="invoice-form-actions">
        <button
          type="submit"
          className="invoice-primary-button"
          disabled={
            loading ||
            loadingProducts ||
            loadingBatchProducts.length > 0
          }
        >
          {loading ? 'Saving Invoice...' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default InvoiceForm;
